import { useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const SUPPORT_THREADS = [
  {
    id: 'payments-desk',
    team: 'Payments Desk',
    role: 'Wallet Support',
    accent: 'Payments',
    online: true,
    priority: 'High',
    unreadCount: 2,
    waitTime: '2 min',
    responseRate: '98%',
    tags: ['Deposit', 'Wallet'],
    preview: 'Your deposit is visible on-chain. We are matching the treasury reference now.',
    lastSeen: '12:42 PM',
    messages: [
      {
        id: 'payments-1',
        sender: 'team',
        time: '11:58 AM',
        text: 'Hi Lucky, we picked up your ticket about a missing wallet credit. I am checking the treasury ledger now.',
      },
      {
        id: 'payments-2',
        sender: 'self',
        time: '12:03 PM',
        text: 'Thanks. I sent 500 USDT through TRC20 and the hash already confirmed.',
        status: 'read'
      },
      {
        id: 'payments-3',
        sender: 'team',
        time: '12:11 PM',
        text: 'Perfect. We can see the transfer on-chain. The remaining step is matching it to your account reference. That usually clears in a couple of minutes.'
      },
      {
        id: 'payments-4',
        sender: 'team',
        time: '12:42 PM',
        text: 'Your deposit is visible on-chain. We are matching the treasury reference now.'
      }
    ]
  },
  {
    id: 'trade-ops',
    team: 'Trade Operations',
    role: 'Execution Support',
    accent: 'Execution',
    online: true,
    priority: 'Normal',
    unreadCount: 0,
    waitTime: '3 min',
    responseRate: '95%',
    tags: ['Order', 'Futures'],
    preview: 'We reviewed the tap-trading round and shared the execution snapshot below.',
    lastSeen: '11:26 AM',
    messages: [
      {
        id: 'trade-1',
        sender: 'team',
        time: '10:14 AM',
        text: 'We reviewed the tap-trading round you flagged. Your order was accepted inside the final pricing window.'
      },
      {
        id: 'trade-2',
        sender: 'team',
        time: '10:15 AM',
        text: 'Execution snapshot: entry 104,233.10, exit 104,240.44, payout 1.85x.'
      },
      {
        id: 'trade-3',
        sender: 'self',
        time: '10:21 AM',
        text: 'Got it. I mainly wanted to confirm there was no latency issue on my side.',
        status: 'read'
      },
      {
        id: 'trade-4',
        sender: 'team',
        time: '11:26 AM',
        text: 'You were clear. No abnormal latency or price desync showed up during that round.'
      }
    ]
  },
  {
    id: 'account-review',
    team: 'Account Review',
    role: 'Verification Team',
    accent: 'KYC',
    online: false,
    priority: 'Pending',
    unreadCount: 1,
    waitTime: '18 min',
    responseRate: '92%',
    tags: ['KYC', 'Security'],
    preview: 'Please resend the proof of address with the full issue date visible.',
    lastSeen: 'Yesterday',
    messages: [
      {
        id: 'account-1',
        sender: 'team',
        time: 'Yesterday',
        text: 'We reviewed your uploaded proof of address. The document is valid, but the issue date is cropped in the current file.'
      },
      {
        id: 'account-2',
        sender: 'team',
        time: 'Yesterday',
        text: 'Please resend the proof of address with the full issue date visible.'
      }
    ]
  },
  {
    id: 'vip-concierge',
    team: 'VIP Concierge',
    role: 'Priority Desk',
    accent: 'VIP',
    online: true,
    priority: 'Live',
    unreadCount: 0,
    waitTime: '<1 min',
    responseRate: '99%',
    tags: ['Strategy', 'Limits'],
    preview: 'We can raise your payout lane after the next rolling-volume review.',
    lastSeen: '9:08 AM',
    messages: [
      {
        id: 'vip-1',
        sender: 'team',
        time: '8:49 AM',
        text: 'Morning. We reviewed your request for higher payout limits.'
      },
      {
        id: 'vip-2',
        sender: 'team',
        time: '9:08 AM',
        text: 'We can raise your payout lane after the next rolling-volume review.'
      }
    ]
  }
];

const QUICK_REPLIES = [
  'Need update on my deposit',
  'Withdrawal still pending',
  'Help with account verification',
  'Order execution question'
];

function getInitials(name) {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}

export function SupportPage() {
  const [searchValue, setSearchValue] = useState('');
  const [threads, setThreads] = useState(SUPPORT_THREADS);
  const [activeThreadId, setActiveThreadId] = useState(SUPPORT_THREADS[0].id);
  const [draftMessage, setDraftMessage] = useState('');
  const [mobilePane, setMobilePane] = useState('inbox');
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');

  const filteredThreads = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return threads;
    }

    return threads.filter((thread) => {
      const haystack = [
        thread.team,
        thread.role,
        thread.preview,
        thread.accent,
        thread.tags.join(' ')
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [searchValue, threads]);

  const activeThread = filteredThreads.length === 0
    ? null
    : filteredThreads.find((thread) => thread.id === activeThreadId) || filteredThreads[0];

  const openTickets = threads.length;
  const unreadTickets = threads.reduce((total, thread) => total + thread.unreadCount, 0);
  const onlineTeams = threads.filter((thread) => thread.online).length;

  const handleSelectThread = (threadId) => {
    setActiveThreadId(threadId);
    setMobilePane('chat');
    setThreads((previous) => previous.map((thread) => (
      thread.id === threadId
        ? { ...thread, unreadCount: 0 }
        : thread
    )));
  };

  const handleSendMessage = () => {
    const message = draftMessage.trim();
    if (!message || !activeThread) {
      return;
    }

    const nextMessage = {
      id: `${activeThread.id}-${Date.now()}`,
      sender: 'self',
      time: 'Now',
      text: message,
      status: 'sent'
    };

    setThreads((previous) => previous.map((thread) => {
      if (thread.id !== activeThread.id) {
        return thread;
      }

      return {
        ...thread,
        preview: message,
        lastSeen: 'Now',
        messages: [...thread.messages, nextMessage]
      };
    }));
    setDraftMessage('');
    setMobilePane('chat');
  };

  const handleCreateTicket = () => {
    const title = ticketTitle.trim();
    const description = ticketDescription.trim();

    if (!title || !description) {
      return;
    }

    const now = Date.now();
    const newThread = {
      id: `ticket-${now}`,
      team: title,
      role: 'New Ticket',
      accent: 'Open',
      online: true,
      priority: 'New',
      unreadCount: 0,
      waitTime: 'Queued',
      responseRate: '93%',
      tags: ['Ticket', 'Support'],
      preview: description,
      lastSeen: 'Now',
      messages: [
        {
          id: `ticket-message-${now}`,
          sender: 'self',
          time: 'Now',
          text: description,
          status: 'sent'
        }
      ]
    };

    setThreads((previous) => [newThread, ...previous]);
    setActiveThreadId(newThread.id);
    setSearchValue('');
    setDraftMessage('');
    setTicketTitle('');
    setTicketDescription('');
    setIsTicketModalOpen(false);
    setMobilePane('chat');
  };

  return (
    <section className="support-page">
      {/* <header className="support-page__header">
        <div>
          <h1>Support Inbox</h1>
          <p>Talk to payments, verification, and trading operations in a fast chat flow built for desktop and mobile.</p>
        </div>
        <div className="support-page__header-actions">
          <Button type="button" className="support-create-ticket-button" onClick={() => setIsTicketModalOpen(true)}>
            Create Ticket
          </Button>
     
        </div>
      </header> */}

      <section className={`support-shell ${mobilePane === 'chat' ? 'is-chat-active' : ''}`}>
        <Card as="aside" className="support-sidebar-panel">
          <div className="support-sidebar-panel__top">
            <div className="support-brand-chip">
              <span className="support-brand-chip__icon">SD</span>
              <div>
                <strong>Fifty Fifty Support</strong>
                <small>Multi-desk live help</small>
              </div>
            </div>
            <div className="support-sidebar-panel__top-actions">

              <button type="button" className="support-archive-chip" onClick={() => setIsTicketModalOpen(true)}>
                New ticket
              </button>
            </div>
          </div>

          <label className="support-search">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <Input
              className="support-search__input"
              placeholder="Search support desks or topics"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </label>

          <div className="support-sidebar-panel__filters">
            <button type="button" className="support-filter-chip is-active">All chats</button>
            <button type="button" className="support-filter-chip">Resolved</button>
          </div>

          <div className="support-thread-list" aria-label="Support conversations">
            {filteredThreads.map((thread) => {
              const isActive = activeThread?.id === thread.id;
              return (
                <button
                  key={thread.id}
                  type="button"
                  className={`support-thread-item ${isActive ? 'is-active' : ''}`}
                  onClick={() => handleSelectThread(thread.id)}
                >
                  <span className="support-thread-item__avatar">{getInitials(thread.team)}</span>
                  <div className="support-thread-item__body">
                    <div className="support-thread-item__meta">
                      <strong>{thread.team}</strong>
                      <span>{thread.lastSeen}</span>
                    </div>
                    <p>{thread.preview}</p>
                    <div className="support-thread-item__footer">
                      <small>{thread.accent}</small>
                      {thread.unreadCount > 0 ? <b>{thread.unreadCount}</b> : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card as="section" className="support-chat-panel">
          {activeThread ? (
            <>
              <header className="support-chat-panel__header">
                <div className="support-chat-panel__identity">
                  <button
                    type="button"
                    className="support-back-button"
                    onClick={() => setMobilePane('inbox')}
                    aria-label="Back to conversations"
                  >
                    ←
                  </button>
                  <span className="support-chat-panel__avatar">{getInitials(activeThread.team)}</span>
                  <div>
                    <strong>{activeThread.team}</strong>
                    <p>{activeThread.role} • {activeThread.online ? 'online now' : 'offline'}</p>
                  </div>
                </div>
              </header>



              <div className="support-message-stream">
                <div className="support-message-stream__divider">
                  <span>Today</span>
                </div>

                {activeThread.messages.map((message) => (
                  <article
                    key={message.id}
                    className={`support-bubble ${message.sender === 'self' ? 'is-self' : 'is-team'}`}
                  >
                    <p>{message.text}</p>
                    <footer>
                      <span>{message.time}</span>
                      {message.status ? <small>{message.status}</small> : null}
                    </footer>
                  </article>
                ))}
              </div>

              {/* <div className="support-quick-replies" aria-label="Quick replies">
                {QUICK_REPLIES.map((reply) => (
                  <button key={reply} type="button" onClick={() => setDraftMessage(reply)}>
                    {reply}
                  </button>
                ))}
              </div> */}

              <div className="support-composer">
                <Textarea
                  className="support-composer__input"
                  placeholder="Write a message to support..."
                  value={draftMessage}
                  onChange={(event) => setDraftMessage(event.target.value)}
                />
                <div className="support-composer__actions">
                  {/* <button type="button" className="support-attach-button">
                    + Attach
                  </button> */}
                  <Button type="button" variant="destructive" className="support-send-button" onClick={handleSendMessage}>
                    Send message
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="support-chat-empty">
              <strong>No support threads match your search</strong>
              <p>Try a different keyword or clear the search to see all desks.</p>
            </div>
          )}
        </Card>
      </section>

      {isTicketModalOpen ? (
        <div className="support-ticket-modal" onClick={() => setIsTicketModalOpen(false)}>
          <Card
            as="section"
            className="support-ticket-modal__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-ticket-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="support-ticket-modal__header">
              <div>
                <h2 id="support-ticket-title">Create Ticket</h2>
                <p>Open a fresh support thread with a clear title and description.</p>
              </div>
              <button
                type="button"
                className="support-ticket-modal__close"
                onClick={() => setIsTicketModalOpen(false)}
                aria-label="Close ticket modal"
              >
                ×
              </button>
            </header>

            <div className="support-ticket-modal__body">
              <label className="support-ticket-field">
                <span>Title</span>
                <Input
                  className="support-ticket-field__input"
                  placeholder="Example: Deposit not credited"
                  value={ticketTitle}
                  onChange={(event) => setTicketTitle(event.target.value)}
                />
              </label>

              <label className="support-ticket-field">
                <span>Description</span>
                <Textarea
                  className="support-ticket-field__textarea"
                  placeholder="Describe the issue, time, amount, and anything support should verify."
                  value={ticketDescription}
                  onChange={(event) => setTicketDescription(event.target.value)}
                />
              </label>
            </div>

            <div className="support-ticket-modal__actions">
              <Button type="button" variant="outline" className="support-ticket-modal__ghost" onClick={() => setIsTicketModalOpen(false)}>
                Cancel
              </Button>
              <Button type="button" className="support-ticket-modal__submit" onClick={handleCreateTicket}>
                Create ticket
              </Button>
            </div>
          </Card>
        </div>
      ) : null}
    </section>
  );
}
